import { Table as AntdTable, TableProps } from 'antd';
import { FilterValue } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

import { getSortLabel, TABLE_SORT_DIRECTIONS_CONFIG } from 'config/tableSort';
import { CellControllerKind } from 'entities/TableControllers';
import { TableCellEntity } from 'entities/TableCell';
import { TableFactoryStore, TableFactoryRowModel } from 'stores/common';
import { DateFilterValue } from './DateFilter';
import { NumberFilterValue } from './NumberFilter';
import { SelectFilterValue } from './SelectFilter';
import { SwitchFilterValue } from './SwitchFilter';
import { TextFilterValue } from './TextFilter';

import { BodyCellWrapper, HeaderCellWrapper } from './CellWrapper';
import s from './FactoryTable.module.scss';
import { SummaryRow } from './SummaryRow';
import { VirtualTableBody, VirtualSummaryRow } from './VirtualTableBody';
import { useColumns, useFactoryTableHeight, useVirtualFiltering } from './hooks';

/**
 * Применяет фильтр к ячейке entity (для виртуализированного режима)
 */
const applyFilterToCell = (
  cellData: TableCellEntity,
  filterValueJson: string
): boolean => {
  try {
    const controllerKind = cellData.controllerKind;
    const controller = cellData.controller as { value?: unknown; options?: unknown[] };
    
    // TEXT / INPUT_STRING фильтры
    if (controllerKind === CellControllerKind.TEXT || controllerKind === CellControllerKind.INPUT_STRING) {
      const filterValue = JSON.parse(filterValueJson) as TextFilterValue;
      const cellValue = typeof controller?.value === 'string' ? controller.value : '';
      
      if (filterValue.mode === 'number') {
        const numValue = parseFloat(cellValue);
        if (isNaN(numValue)) return false;
        const { from, to, strict } = filterValue;
        if (from !== undefined && (strict ? numValue <= from : numValue < from)) return false;
        if (to !== undefined && (strict ? numValue >= to : numValue > to)) return false;
        return true;
      } else {
        const search = filterValue.search || '';
        if (!search.trim()) return true;
        return cellValue.toLowerCase().includes(search.toLowerCase());
      }
    }
    
    // NUMBER / INPUT_NUMBER фильтры
    if (controllerKind === CellControllerKind.NUMBER || controllerKind === CellControllerKind.INPUT_NUMBER) {
      const filterValue = JSON.parse(filterValueJson) as NumberFilterValue;
      let cellValue: number | undefined;
      
      if (typeof controller?.value === 'number') {
        cellValue = controller.value;
      } else if (typeof controller?.value === 'object' && controller?.value !== null && 'value' in (controller.value as object)) {
        cellValue = (controller.value as { value: number }).value;
      } else if (typeof controller?.value === 'string') {
        cellValue = parseFloat(controller.value);
      }
      
      if (cellValue === undefined || isNaN(cellValue)) return false;
      
      const { from, to, strict } = filterValue;
      if (from !== undefined && (strict ? cellValue <= from : cellValue < from)) return false;
      if (to !== undefined && (strict ? cellValue >= to : cellValue > to)) return false;
      return true;
    }
    
    // SELECT фильтры
    if (controllerKind === CellControllerKind.SELECT) {
      const filterValue = JSON.parse(filterValueJson) as SelectFilterValue;
      const value = controller?.value as { value?: string } | null;
      const cellValue = value?.value;
      
      if (filterValue.selected.length === 0) return true;
      if (!cellValue) return false;
      return filterValue.selected.includes(cellValue);
    }
    
    // SWITCH фильтры
    if (controllerKind === CellControllerKind.SWITCH) {
      const filterValue = JSON.parse(filterValueJson) as SwitchFilterValue;
      const cellValue = controller?.value as boolean | undefined;
      
      if (filterValue.selected.length === 0) return true;
      if (cellValue === undefined) return false;
      return filterValue.selected.includes(cellValue);
    }
    
    // INPUT_DATE фильтры
    if (controllerKind === CellControllerKind.INPUT_DATE) {
      const filterValue = JSON.parse(filterValueJson) as DateFilterValue;
      const rawValue = controller?.value;
      
      if (!rawValue) return false;
      
      const cellValue = typeof rawValue === 'string' ? dayjs(rawValue) : rawValue;
      if (!cellValue || !(cellValue as { isValid?: () => boolean }).isValid?.()) return false;
      
      const { from, to } = filterValue;
      if (from && (cellValue as dayjs.Dayjs).isBefore(dayjs(from))) return false;
      if (to && (cellValue as dayjs.Dayjs).isAfter(dayjs(to))) return false;
      return true;
    }
    
    return true;
  } catch {
    return true;
  }
};

type Props = {
  tableStore: TableFactoryStore;
  className?: TableProps<any>['className'];
  expandIcon: TableProps<any>['expandIcon'];
  rowKey: TableProps<any>['rowKey'];
  pagination?: TableProps<any>['pagination'];
  rowClassName?: TableProps<any>['rowClassName'];
  sortDirections?: TableProps<any>['sortDirections'];
  onChange?: TableProps<any>['onChange'];
  /** Включить виртуализацию таблицы для больших списков */
  virtual?: boolean;
};

const components: TableProps<any>['components'] = {
  header: {
    cell: HeaderCellWrapper,
  },
  body: {
    cell: BodyCellWrapper,
  },
};

// Компоненты для виртуализированного режима - скрываем body
const virtualHeaderComponents: TableProps<any>['components'] = {
  header: {
    cell: HeaderCellWrapper,
  },
  body: {
    wrapper: () => null, // Полностью скрываем tbody
  },
};

function FactoryTable({
  tableStore,
  virtual = false,
  ...tableProps
}: Props): React.ReactElement {
  const { ref, y, id } = useFactoryTableHeight();
  
  // Enable virtualized mode on the store when virtual prop is true
  // Use useLayoutEffect to ensure this runs before any data fetching
  React.useLayoutEffect(() => {
    tableStore.setVirtualizedMode(virtual);
  }, [tableStore, virtual]);
  
  // Состояние фильтров для виртуального режима
  const [virtualFilters, setVirtualFilters] = React.useState<Record<string, FilterValue> | null>(null);

  const renderColumns = useColumns({
    columns: tableStore.renderColumns,
    tableParams: tableStore.tableParams,
    rows: tableStore.rows,
    rowDataStore: tableStore.rowDataStore,
  });
  
  // Создаём маппинг название колонки -> ID колонки для фильтрации
  const columnNameToIdMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    tableStore.renderColumns.forEach((col) => {
      map[col.data.name] = col.data.id;
    });
    return map;
  }, [tableStore.renderColumns]);
  
  // Применяем фильтры для виртуального режима
  const filteredRows = useVirtualFiltering(tableStore.rows, virtualFilters, columnNameToIdMap);

  const summaryRow = React.useCallback(() => {
    return (
      <SummaryRow
        summaryRow={tableStore.tableAggregationStore.aggregationFooterRowModel}
        columns={tableStore.renderColumns}
        tableParams={tableStore.tableParams}
      />
    );
  }, [
    tableStore.renderColumns,
    tableStore.tableAggregationStore.aggregationFooterRowModel,
    tableStore.tableParams,
  ]);

  const handleParamsChange = React.useCallback(
    async (
      ...[pagination, filter, sort]: Parameters<
        NonNullable<TableProps<any>['onChange']>
      >
    ) => {
      // Сохраняем фильтры для виртуального режима
      if (virtual && filter) {
        setVirtualFilters(filter);
      }
      
      await tableStore.handleTableParamsChange({
        sort,
        filter,
        pagination,
      });
    },
    [tableStore, virtual]
  );

  // Ref для синхронизации скролла между header и virtual body
  const headerContainerRef = React.useRef<HTMLDivElement>(null);

  // Get row IDs from data store for lazy model creation (virtualized mode)
  // Apply filtering if filters are active
  const rowIds = React.useMemo(() => {
    const allRowIds = tableStore.rowIds;
    
    // If no filters, return all row IDs
    if (!virtualFilters || Object.keys(virtualFilters).length === 0) {
      return allRowIds;
    }
    
    // Apply filters by checking each row
    return allRowIds.filter((rowId) => {
      const entity = tableStore.rowDataStore.getRowEntity(rowId);
      if (!entity) return false;
      
      // Check all active filters
      for (const [columnName, filterValues] of Object.entries(virtualFilters)) {
        if (!filterValues || filterValues.length === 0) continue;
        
        const columnId = columnNameToIdMap[columnName];
        if (!columnId) continue;
        
        const cellData = entity.columns[columnId];
        if (!cellData) return false;
        
        // Apply each filter value (they are JSON strings with filter config)
        // Row passes if ANY of the filter values match (OR logic within a column)
        const filterPassed = filterValues.some((filterValueJson) => {
          if (typeof filterValueJson !== 'string') return false;
          return applyFilterToCell(cellData, filterValueJson);
        });
        
        if (!filterPassed) {
          return false;
        }
      }
      
      return true;
    });
  }, [tableStore.rowIds, tableStore.rowDataStore, virtualFilters, columnNameToIdMap]);

  // Вычисляем общую ширину столбцов для синхронизации header и body
  const totalColumnsWidth = React.useMemo(() => {
    return renderColumns.reduce((sum, col) => {
      const width = col.width;
      return sum + (typeof width === 'number' ? width : 200);
    }, 0);
  }, [renderColumns]);

  // Для виртуализированного режима рендерим кастомный body
  if (virtual && y > 0) {
    return (
      <div id={id} ref={ref} style={{ overflow: 'hidden', width: '100%' }}>
        <div ref={headerContainerRef} className={s.headerContainer}>
          <AntdTable
            components={virtualHeaderComponents}
            dataSource={[]} // Пустой dataSource для header
            columns={renderColumns}
            onChange={handleParamsChange}
            sortDirections={TABLE_SORT_DIRECTIONS_CONFIG}
            showSorterTooltip={{
              title: getSortLabel(tableStore.sortDirection),
            }}
            scroll={{
              x: totalColumnsWidth,
            }}
            pagination={false}
            locale={{ emptyText: null }}
            {...tableProps}
          />
        </div>
        <VirtualTableBody
          columns={renderColumns}
          scroll={{ y }}
          rowIds={rowIds}
          store={tableStore}
          headerRef={headerContainerRef}
          loading={tableStore.fetching}
        />
        <VirtualSummaryRow
          summaryRow={tableStore.tableAggregationStore.aggregationFooterRowModel}
          columns={tableStore.renderColumns}
          tableParams={tableStore.tableParams}
        />
      </div>
    );
  }

  return (
    <AntdTable
      id={id}
      ref={ref}
      components={components}
      dataSource={tableStore.rows}
      columns={renderColumns}
      expandable={{
        expandedRowKeys: tableStore.expandedRowsKeys,
      }}
      onChange={handleParamsChange}
      sortDirections={TABLE_SORT_DIRECTIONS_CONFIG}
      showSorterTooltip={{
        title: getSortLabel(tableStore.sortDirection),
      }}
      scroll={{
        x: 'max-content',
        y,
      }}
      loading={tableStore.fetching}
      summary={summaryRow}
      {...tableProps}
    />
  );
}

export default observer(FactoryTable);
